import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { EntityFilter, SearchCandidate, SearchTranslation } from '@metron/shared';
import {
  entityFilterSchema,
  searchTranslationSchema,
  toCustomerSearchParams,
  toCustomGroupSearchParams,
  toEndConsumerSearchParams,
  toMeterSearchParams,
  toPropertySearchParams,
  toUserSearchParams,
} from '@metron/shared';
import { AlertTriangle, Clock, Loader2, Search } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog.tsx';
import { Button } from '../ui/button.tsx';
import { Input } from '../ui/input.tsx';
import { useApiClient } from '../../hooks/useApiClient.ts';

/** Where a translated filter's results live. Exhaustive over `SEARCH_ENTITIES`. */
export function resultsUrl(target: EntityFilter): string {
  switch (target.entity) {
    case 'meters':
      return `/search/meters?${toMeterSearchParams(target.filter)}`;
    case 'customers':
      return `/search/customers?${toCustomerSearchParams(target.filter)}`;
    case 'properties':
      return `/search/properties?${toPropertySearchParams(target.filter)}`;
    case 'customGroups':
      return `/search/custom-groups?${toCustomGroupSearchParams(target.filter)}`;
    case 'endConsumers':
      return `/search/end-consumers?${toEndConsumerSearchParams(target.filter)}`;
    case 'users':
      return `/search/users?${toUserSearchParams(target.filter)}`;
  }
}

const CANDIDATE_NOUN: Record<SearchCandidate['type'], string> = {
  customer: 'customer',
  property: 'property',
  sensorGroup: 'sensor group',
};

/**
 * Re-ask with the chosen candidate spelled out. The request only carries text,
 * and the translator only trusts ids it resolved itself, so the choice goes
 * back as words it can resolve exactly.
 */
export function withChoice(query: string, candidate: SearchCandidate): string {
  return `${query} (${CANDIDATE_NOUN[candidate.type]}: ${candidate.label})`;
}

// ---------------------------------------------------------------- recent searches

const RECENT_KEY = 'wellspring.smartSearch.recent';
const RECENT_LIMIT = 5;

// Per-viewer convenience only: storage can be missing or throw (private
// windows, blocked site data), and the dialog works the same without it.
export function readRecent(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((q): q is string => typeof q === 'string') : [];
  } catch {
    return [];
  }
}

export function rememberRecent(query: string): void {
  try {
    const next = [query, ...readRecent().filter((q) => q !== query)].slice(0, RECENT_LIMIT);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    // Storage unavailable; nothing to remember.
  }
}

// ---------------------------------------------------------------- dialog

type Outcome =
  | { kind: 'idle' }
  | { kind: 'pending' }
  | { kind: 'error'; message: string }
  | Exclude<SearchTranslation, { kind: 'filter' }>;

interface SmartSearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Smart Search (MET-3508): type a request, get sent to the results page with
 * the translated filter in the URL. Clarifications and unsupported asks are
 * resolved inline. A new submit or closing the dialog cancels the request in
 * flight, so only the latest answer is ever acted on.
 */
export function SmartSearchDialog({ open, onOpenChange }: SmartSearchDialogProps) {
  const apiClient = useApiClient();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [outcome, setOutcome] = useState<Outcome>({ kind: 'idle' });
  const [recent, setRecent] = useState<string[]>([]);
  const inFlight = useRef<AbortController | null>(null);

  const cancel = useCallback(() => {
    inFlight.current?.abort();
    inFlight.current = null;
  }, []);

  // Fresh state each time the dialog opens; cancel anything left when it closes.
  useEffect(() => {
    if (open) {
      setQuery('');
      setRecent(readRecent());
      setOutcome({ kind: 'idle' });
    } else {
      cancel();
    }
  }, [open, cancel]);

  useEffect(() => cancel, [cancel]);

  const go = (target: EntityFilter, asked: string) => {
    rememberRecent(asked);
    onOpenChange(false);
    navigate(resultsUrl(target));
  };

  const submit = async (asked: string) => {
    const text = asked.trim();
    if (!text) return;
    cancel();
    const controller = new AbortController();
    inFlight.current = controller;
    setQuery(text);
    setOutcome({ kind: 'pending' });

    try {
      const raw = await apiClient.post<unknown>('search/translate', { query: text }, {
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      const parsed = searchTranslationSchema.safeParse(raw);
      if (!parsed.success) {
        setOutcome({ kind: 'error', message: 'Smart Search returned something unexpected.' });
        return;
      }
      const result = parsed.data;
      if (result.kind === 'filter') {
        // Pair entity with filter so the filter narrows to that entity's spec.
        const target = entityFilterSchema.safeParse({
          entity: result.entity,
          filter: result.filter,
        });
        if (!target.success) {
          setOutcome({ kind: 'error', message: 'Smart Search returned something unexpected.' });
          return;
        }
        go(target.data, text);
        return;
      }
      setOutcome(result);
    } catch (err) {
      if (controller.signal.aborted) return;
      setOutcome({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Smart Search failed.',
      });
    } finally {
      if (inFlight.current === controller) inFlight.current = null;
    }
  };

  const pending = outcome.kind === 'pending';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-xl'>
        <DialogHeader>
          <DialogTitle>Smart Search</DialogTitle>
          <DialogDescription>
            Describe what you're looking for, e.g. “meters at Springfield with no contact in 3
            days”.
          </DialogDescription>
        </DialogHeader>

        <div
          role='note'
          aria-label='Beta feature'
          className='flex items-start gap-2 rounded-lg border border-amber-600 bg-amber-50 dark:bg-amber-950 px-3 py-2 text-sm text-amber-900 dark:text-amber-200'
        >
          <AlertTriangle
            className='h-4 w-4 mt-0.5 flex-shrink-0 text-amber-600'
            aria-hidden='true'
          />
          <p>
            <strong>Beta feature.</strong>{' '}
            Results may be incomplete or wrong, so check them before acting on them.{' '}
            <Link
              to='/docs/smart-search'
              className='font-medium underline underline-offset-2'
              onClick={() => onOpenChange(false)}
            >
              Read the Smart Search guide
            </Link>{' '}
            for what it can do and who to contact about problems.
          </p>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submit(query);
          }}
          className='flex gap-2'
        >
          <Input
            aria-label='Search request'
            placeholder='Search meters or customers…'
            value={query}
            autoFocus
            onChange={(e) => {
              setQuery(e.target.value);
              // Typing a new request supersedes the one in flight.
              if (pending) {
                cancel();
                setOutcome({ kind: 'idle' });
              }
            }}
          />
          <Button type='submit' disabled={!query.trim() || pending}>
            {pending
              ? <Loader2 className='h-4 w-4 animate-spin' aria-hidden='true' />
              : <Search className='h-4 w-4' aria-hidden='true' />}
            <span className='sr-only sm:not-sr-only sm:ml-1'>Search</span>
          </Button>
        </form>

        <div aria-live='polite' className='min-h-6 text-sm'>
          {pending && <p className='text-muted-foreground'>Working out your search…</p>}

          {outcome.kind === 'error' && (
            <p role='alert' className='text-destructive'>{outcome.message}</p>
          )}

          {outcome.kind === 'clarify' && (
            <div className='space-y-2'>
              <p>{outcome.question}</p>
              <ul className='flex flex-wrap gap-2'>
                {outcome.candidates.map((c) => (
                  <li key={`${c.type}-${c.id}`}>
                    <Button
                      type='button'
                      variant='outline'
                      size='sm'
                      onClick={() => void submit(withChoice(query, c))}
                    >
                      {c.label}
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {outcome.kind === 'unsupported' && (
            <div className='space-y-2'>
              <p>{outcome.reason}</p>
              {outcome.partial && (
                <Button
                  type='button'
                  size='sm'
                  onClick={() => outcome.partial && go(outcome.partial, query)}
                >
                  Search with what I understood
                </Button>
              )}
            </div>
          )}

          {outcome.kind === 'idle' && recent.length > 0 && (
            <div className='space-y-1'>
              <p className='text-xs uppercase tracking-wide text-muted-foreground'>Recent</p>
              <ul>
                {recent.map((q) => (
                  <li key={q}>
                    <button
                      type='button'
                      className='flex w-full items-center gap-2 rounded px-2 py-1 text-left hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                      onClick={() => void submit(q)}
                    >
                      <Clock className='h-3.5 w-3.5 text-muted-foreground' aria-hidden='true' />
                      {q}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** The shortcut as the viewer's platform writes it. */
export function shortcutHint(userAgent: string = globalThis.navigator?.userAgent ?? ''): string {
  return /Mac|iPhone|iPad/.test(userAgent) ? '⌘K' : 'Ctrl K';
}

/** ⌘K / Ctrl+K toggles Smart Search from anywhere in the app, when `enabled`. */
export function useSmartSearchShortcut(
  enabled: boolean,
  setOpen: (update: (open: boolean) => boolean) => void,
) {
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      // Holding the keys auto-repeats; only the first press toggles.
      if (!e.repeat && (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };
    globalThis.addEventListener('keydown', onKey);
    return () => globalThis.removeEventListener('keydown', onKey);
  }, [enabled, setOpen]);
}
